import React from 'react';
import { Settings, MonitorPlay, ClipboardList, Briefcase, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

const SkillToDeploy = () => {
    return (
        <div className="bg-brand-bg pt-8 pb-8 relative overflow-hidden font-sans">

            {/* Background Decorations */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-30 pointer-events-none">
                <motion.div
                    animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.3, 0.5, 0.3],
                        rotate: [0, 45, 0],
                    }}
                    transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute top-0 left-1/4 w-96 h-96 bg-brand-bg/50 rounded-full blur-3xl"
                />
                <motion.div
                    animate={{
                        scale: [1, 1.1, 1],
                        opacity: [0.3, 0.5, 0.3],
                        rotate: [0, -45, 0],
                    }}
                    transition={{ duration: 18, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                    className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-200/50 rounded-full blur-3xl"
                />
            </div>

            <div className="max-w-7xl mx-auto px-6 relative z-10">
                {/* Section Header - High Impact */}
                <div className="text-center mb-20 max-w-4xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="inline-block mb-4"
                    >
                        <span className="bg-brand-gold text-black text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-widest border border-blue-200 shadow-sm">
                            Proven Career Pathway
                        </span>
                    </motion.div>

                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        viewport={{ once: true }}
                        className="text-brand-goldxl font-bold text-brand-textMuted mb-6 leading-tight"
                    >
                        Transform Your Potential into <span className="text-brand-gold">Professional Success</span>
                    </motion.h2>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        viewport={{ once: true }}
                        className="text-lg text-brand-textMuted md:text-xl max-w-2xl mx-auto leading-relaxed"
                    >
                        Our structured "Skill to Deploy" framework bridges the gap between learning and your dream career with guaranteed outcomes.
                    </motion.p>
                </div>

                {/* Desktop Circular Layout */}
                <div className="hidden md:flex justify-center items-center relative min-h-[600px]">


                    {/* Connecting Rings & Arcs - Desktop */}
                    <svg className="absolute w-full h-full pointer-events-none z-0" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet">
                        <defs>
                            <linearGradient id="arc-gradient-1" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="#22d3ee" stopOpacity="0" />
                                <stop offset="50%" stopColor="#22d3ee" stopOpacity="1" />
                                <stop offset="100%" stopColor="#22d3ee" stopOpacity="0" />
                            </linearGradient>
                            <linearGradient id="arc-gradient-2" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="#60a5fa" stopOpacity="0" />
                                <stop offset="50%" stopColor="#60a5fa" stopOpacity="1" />
                                <stop offset="100%" stopColor="#60a5fa" stopOpacity="0" />
                            </linearGradient>
                        </defs>

                        {/* Dashed Orbital Path */}
                        <motion.circle
                            initial={{ pathLength: 0, opacity: 0 }}
                            whileInView={{ pathLength: 1, opacity: 0.6 }}
                            transition={{ duration: 2, ease: "easeInOut" }}
                            viewport={{ once: true }}
                            cx="400" cy="300" r="180" fill="none" stroke="#e2e8f0" strokeWidth="2" strokeDasharray="6 6" className="opacity-60"
                        />

                        {/* Top Right Arc (Virtual Internship) */}
                        <motion.path
                            d="M 400 120 A 180 180 0 0 1 580 300"
                            fill="none"
                            stroke="url(#arc-gradient-1)"
                            strokeWidth="5"
                            strokeLinecap="round"
                            className="drop-shadow-xl"
                            initial={{ pathLength: 0 }}
                            whileInView={{ pathLength: 1 }}
                            transition={{ duration: 1.5, delay: 0.5, ease: "easeInOut" }}
                            viewport={{ once: true }}
                        />

                        {/* Bottom Left Arc (Unlimited Placement) */}
                        <motion.path
                            d="M 400 480 A 180 180 0 0 1 220 300"
                            fill="none"
                            stroke="url(#arc-gradient-2)"
                            strokeWidth="5"
                            strokeLinecap="round"
                            className="drop-shadow-xl"
                            initial={{ pathLength: 0 }}
                            whileInView={{ pathLength: 1 }}
                            transition={{ duration: 1.5, delay: 0.5, ease: "easeInOut" }}
                            viewport={{ once: true }}
                        />

                        {/* Decorative dots on arcs */}
                        <motion.g initial={{ scale: 0 }} whileInView={{ scale: 1 }} transition={{ delay: 2, duration: 0.3 }}>
                            <motion.circle
                                cx="580" cy="300" r="8" fill="#22d3ee"
                                animate={{ scale: [1, 1.5, 1], opacity: [1, 0.8, 1] }}
                                transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 1 }}
                            />
                        </motion.g>
                        <motion.g initial={{ scale: 0 }} whileInView={{ scale: 1 }} transition={{ delay: 2, duration: 0.3 }}>
                            <motion.circle
                                cx="220" cy="300" r="8" fill="#60a5fa"
                                animate={{ scale: [1, 1.5, 1], opacity: [1, 0.8, 1] }}
                                transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 1, delay: 0.5 }}
                            />
                        </motion.g>
                    </svg>

                    {/* Orbiting Particles */}
                    <div className="absolute inset-0 pointer-events-none z-0">
                        <motion.div
                            className="absolute top-1/2 left-1/2 w-[360px] h-[360px] -translate-x-1/2 -translate-y-1/2 rounded-full"
                            animate={{ rotate: 360 }}
                            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                        >
                            <div className="absolute top-0 right-1/2 w-3 h-3 bg-blue-400 rounded-full blur-[2px]"></div>
                        </motion.div>
                        <motion.div
                            className="absolute top-1/2 left-1/2 w-[360px] h-[360px] -translate-x-1/2 -translate-y-1/2 rounded-full"
                            animate={{ rotate: -360 }}
                            transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                        >
                            <div className="absolute bottom-0 left-1/2 w-2 h-2 bg-purple-400 rounded-full blurbg-brand-gold"></div>
                        </motion.div>
                    </div>

                    {/* Central Circle */}
                    <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        whileInView={{ scale: 1, opacity: 1 }}
                        transition={{ type: "spring", stiffness: 260, damping: 20, duration: 0.8 }}
                        viewport={{ once: true }}
                        className="relative z-20 w-64 h-64 rounded-full bg-brand-card shadow-[0_0_80px_-10px_rgba(37,99,235,0.4)] flex flex-col items-center justify-center border-4 border-gray-800"
                    >
                        <div className="absolute inset-0 rounded-full border-2 border-blue-50/50 animate-[spin_8s_linear_infinite]"></div>
                        <div className="absolute inset-2 rounded-full border border-dashed border-blue-200 animate-[spin_15s_linear_infinite_reverse]"></div>

                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5, duration: 0.5 }}
                            className="text-center z-10"
                        >
                            <h2 className="text-4xl font-extrabold text-brand-textLight leading-tight">
                                Skill to<br />
                                <span className="text-brand-gold">Deploy</span>
                            </h2>
                        </motion.div>

                        {/* Subtle inner pulse */}
                        <div className="absolute inset-4 rounded-full border border-blue-100/30 animate-ping opacity-20"></div>
                    </motion.div>

                    {/* Item 1: Top Left */}
                    <motion.div
                        initial={{ opacity: 0, x: -50, y: -50 }}
                        whileInView={{ opacity: 1, x: 0, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.5 }}
                        className="absolute top-[8%] left-[12%] lg:left-[18%] max-w-xs text-right group cursor-pointer"
                    >
                        <motion.div
                            animate={{ y: [0, -12, 0] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0 }}
                            whileHover={{ scale: 1.1 }}
                        >
                            <div className="flex items-center justify-end gap-3 mb-2">
                                <div className="w-14 h-14 bg-brand-card rounded-xl shadow-lg border border-blue-50 flex items-center justify-center z-10 group-hover:-translate-y-1 transition-transform relative overflow-hidden">
                                    <motion.div className="absolute inset-0 bg-blue-50/50" initial={{ scale: 0 }} whileHover={{ scale: 1.5 }} transition={{ duration: 0.3 }} />
                                    <Settings className="w-7 h-7 text-brand-gold relative z-10" />
                                </div>
                            </div>
                            <h4 className="text-lg font-bold text-brand-textLight leading-tight">6 Months Intensive<br />Skill Training</h4>
                            <span className="text-xs font-semibold text-brand-gold bg-blue-50 px-2 py-0.5 rounded-full mt-1 inline-block">
                                + 150 Industry Projects
                            </span>
                        </motion.div>
                    </motion.div>

                    {/* Item 2: Top Right */}
                    <motion.div
                        initial={{ opacity: 0, x: 50, y: -50 }}
                        whileInView={{ opacity: 1, x: 0, y: 0 }}
                        transition={{ delay: 0.4, duration: 0.5 }}
                        className="absolute top-[18%] right-[8%] lg:right-[15%] max-w-xs text-left group cursor-pointer"
                    >
                        <motion.div
                            animate={{ y: [0, -12, 0] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                            whileHover={{ scale: 1.1 }}
                        >
                            <div className="flex items-center justify-start gap-3 mb-2">
                                <div className="w-14 h-14 bg-brand-card rounded-xl shadow-lg border border-cyan-50 flex items-center justify-center z-10 group-hover:-translate-y-1 transition-transform relative overflow-hidden">
                                    <motion.div className="absolute inset-0 bg-cyan-50/50" initial={{ scale: 0 }} whileHover={{ scale: 1.5 }} transition={{ duration: 0.3 }} />
                                    <MonitorPlay className="w-7 h-7 text-brand-textMuted relative z-10" />
                                </div>
                            </div>
                            <h3 className="text-xl font-bold text-brand-textLight">2 Months</h3>
                            <h4 className="text-lg font-bold text-brand-textMuted">Virtual Internship</h4>
                        </motion.div>
                    </motion.div>

                    {/* Item 3: Bottom Right */}
                    <motion.div
                        initial={{ opacity: 0, x: 50, y: 50 }}
                        whileInView={{ opacity: 1, x: 0, y: 0 }}
                        transition={{ delay: 0.6, duration: 0.5 }}
                        className="absolute bottom-[8%] right-[8%] lg:right-[14%] max-w-xs text-left group cursor-pointer"
                    >
                        <motion.div
                            animate={{ y: [0, -12, 0] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                            whileHover={{ scale: 1.1 }}
                        >
                            <div className="flex items-center justify-start gap-3 mb-2">
                                <div className="w-14 h-14 bg-brand-card rounded-xl shadow-lg border border-cyan-50 flex items-center justify-center z-10 group-hover:-translate-y-1 transition-transform relative overflow-hidden">
                                    <motion.div className="absolute inset-0 bg-cyan-50/50" initial={{ scale: 0 }} whileHover={{ scale: 1.5 }} transition={{ duration: 0.3 }} />
                                    <ClipboardList className="w-7 h-7 text-cyan-600 relative z-10" />
                                </div>
                            </div>
                            <h4 className="text-lg font-bold text-brand-textLight leading-tight mb-1">3 Rigorous Tests</h4>
                            <div className="text-xs text-gray-500 font-medium space-y-0.5">
                                <div className="flex items-center gap-1.5"><span className="w-1 h-1 bg-brand-gold rounded-full"></span>Communication</div>
                                <div className="flex items-center gap-1.5"><span className="w-1 h-1 bg-brand-gold rounded-full"></span>Business Logic</div>
                                <div className="flex items-center gap-1.5"><span className="w-1 h-1 bg-brand-gold rounded-full"></span>Aptitude</div>
                            </div>
                        </motion.div>
                    </motion.div>


                    {/* Item 4: Bottom Left */}
                    <motion.div
                        initial={{ opacity: 0, x: -50, y: 50 }}
                        whileInView={{ opacity: 1, x: 0, y: 0 }}
                        transition={{ delay: 0.8, duration: 0.5 }}
                        className="absolute bottom-[10%] left-[8%] lg:left-[15%] max-w-xs text-right group cursor-pointer"
                    >
                        <motion.div
                            animate={{ y: [0, -12, 0] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 3 }}
                            whileHover={{ scale: 1.1 }}
                        >
                            <div className="flex items-center justify-end gap-3 mb-2">
                                <div className="w-14 h-14 bg-brand-card rounded-xl shadow-lg border border-indigo-50 flex items-center justify-center z-10 group-hover:-translate-y-1 transition-transform relative overflow-hidden">
                                    <motion.div className="absolute inset-0 bg-indigo-50/50" initial={{ scale: 0 }} whileHover={{ scale: 1.5 }} transition={{ duration: 0.3 }} />
                                    <Briefcase className="w-7 h-7 text-indigo-600 relative z-10" />
                                </div>
                            </div>
                            <h4 className="text-lg font-bold text-brand-textLight leading-tight">Unlimited Placement<br />Opportunities</h4>
                            <p className="text-xs text-gray-500 font-medium mt-1">Until You Get Placed</p>
                        </motion.div>
                    </motion.div>
                </div>

                {/* Mobile Linear Layout */}
                <div className="md:hidden space-y-6">
                    {[
                        {
                            id: 1,
                            title: "Intensive Skill Training",
                            subtitle: "6 Months + 150 Industry Projects",
                            icon: <Settings className="w-6 h-6 text-black" />,
                            color: "bg-brand-gold"
                        },
                        {
                            id: 2,
                            title: "Virtual Internship",
                            subtitle: "2 Months Real-world Experience",
                            icon: <MonitorPlay className="w-6 h-6 text-black" />,
                            color: "bg-purple-600"
                        },
                        {
                            id: 3,
                            title: "3 Rigorous Tests",
                            subtitle: "Communication, Business Logic, Aptitude",
                            icon: <ClipboardList className="w-6 h-6 text-black" />,
                            color: "bg-cyan-600"
                        },
                        {
                            id: 4,
                            title: "Unlimited Opportunities",
                            subtitle: "Until You Get Placed",
                            icon: <Briefcase className="w-6 h-6 text-black" />,
                            color: "bg-indigo-600"
                        },
                    ].map((step, idx) => (
                        <motion.div
                            key={step.id}
                            whileHover={{ scale: 1.02 }}
                            className="bg-brand-card p-6 rounded-2xl shadow-sm border border-gray-800 flex items-start gap-4"
                        >
                            <div className={`${step.color} w-12 h-12 rounded-xl flex items-center justify-center shrink-0 shadow-md`}>
                                {step.icon}
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-brand-textLight leading-tight mb-1">{step.title}</h3>
                                <p className="text-brand-textMuted text-sm">{step.subtitle}</p>
                            </div>
                        </motion.div>
                    ))}

                    <div className="text-center mt-8">
                        <button className="bg-brand-gold text-black px-8 py-3 rounded-full font-bold inline-flex items-center gap-2 hover:bg-brand-goldHover transition-colors shadow-lg">
                            Get Started <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SkillToDeploy;
